// Measurement abbreviations are language-neutral and are intentionally kept
// untranslated throughout the work order (the same way dimension strings like
// "8sqft" are). Stripping a trailing measurement off a name/description before
// translation — and re-appending it afterwards — keeps it from being fed to the
// (non-deterministic) note translator, which would otherwise render the same
// unit inconsistently (e.g. "8ft" in one area, "8 pies" in another).
//
// Covered units are exactly the set the app generates (getUnitAbbreviation):
// ft / sqft / lnft / cuft (imperial) and m / m² / m³ (metric), plus the
// foot/inch marks a contractor is likely to type by hand in a custom substrate
// name ("8'", "8\"", or compound "8'6\""). Listed longest/most-specific first so
// e.g. "8sqft" matches "sqft", not "ft".
const MEASUREMENT_UNITS = ['sqft', 'lnft', 'cuft', 'm²', 'm³', 'ft', 'm']

// Straight, curly, and prime variants of the foot (') and inch (") marks.
const FOOT_MARKS = ["'", '’', '′']
const INCH_MARKS = ['"', '”', '″']

const isDigit = (char: string) => char >= '0' && char <= '9'
const isSpace = (char: string) => char === ' ' || char === '\t'

// Consume a number (with an optional single decimal point) ending at `end`,
// returning the start index, or -1 if there is no number there.
function consumeNumber(text: string, end: number): number {
  let i = end
  let sawDigit = false
  let sawDot = false
  while (i > 0) {
    const char = text[i - 1]
    if (isDigit(char)) {
      sawDigit = true
      i--
    } else if (char === '.' && !sawDot) {
      sawDot = true
      i--
    } else {
      break
    }
  }
  return sawDigit ? i : -1
}

/**
 * Splits a trailing measurement token off the end of a string. Returns the
 * translatable base and the measurement (including its leading whitespace) so
 * the measurement can be re-appended after translation.
 *
 * Recognized forms (case-insensitive), each optionally followed by a period:
 *   "Walls 8ft", "Walls 8 ft", "Ceiling 12.5 sqft", "Wall 3m²"
 *   "Walls 8'", "Trim 6\"", "Walls 8'6\"" (and curly/prime mark variants)
 *
 * A token only counts as a measurement when it is `<base> <number><unit>`: there
 * must be a number, a whitespace separator, and a non-empty base in front of it.
 * Strings that are only a measurement (e.g. "8ft") or have no unit (e.g.
 * "Coat 2") are returned unchanged. Spelled-out words ("feet", "metres") are
 * deliberately left to translate normally.
 */
export function splitTrailingMeasurement(text: string | null | undefined): { base: string; measurement: string } {
  const value = text ?? ''
  const noMatch = { base: value, measurement: '' }

  // Allow an optional trailing period (e.g. "8 ft.") without it defeating the unit match.
  const work = value.endsWith('.') ? value.slice(0, -1) : value

  let i = work.length
  let matchedAny = false
  // Peel trailing measurement segments. The loop handles compound feet+inches
  // ("8'6\"") by peeling the inch segment, then the foot segment. An abbreviation
  // is always the terminal unit; only mark segments chain.
  let keepPeeling = true

  while (keepPeeling) {
    keepPeeling = false

    let unitEnd = i
    while (unitEnd > 0 && isSpace(work[unitEnd - 1])) unitEnd--
    if (unitEnd === 0) break

    const lower = work.slice(0, unitEnd).toLowerCase()
    const abbrev = MEASUREMENT_UNITS.find((u) => lower.endsWith(u))
    const lastChar = work[unitEnd - 1]
    const isMark = FOOT_MARKS.includes(lastChar) || INCH_MARKS.includes(lastChar)

    const unitLength = abbrev ? abbrev.length : isMark ? 1 : 0
    if (unitLength === 0) break

    // Optional whitespace between the number and the unit ("8 ft").
    let numberEnd = unitEnd - unitLength
    while (numberEnd > 0 && isSpace(work[numberEnd - 1])) numberEnd--

    const numberStart = consumeNumber(work, numberEnd)
    if (numberStart === -1) break

    i = numberStart
    matchedAny = true
    keepPeeling = !abbrev
  }

  if (!matchedAny) return noMatch

  // Require whitespace separating the measurement from the base, then a non-empty base.
  if (i === 0 || !isSpace(work[i - 1])) return noMatch
  while (i > 0 && isSpace(work[i - 1])) i--
  if (i === 0) return noMatch

  const base = value.slice(0, i)
  if (!base.trim()) return noMatch

  return { base, measurement: value.slice(i) }
}
